import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import logoConnect10 from '../../assets/logo-connect10.png';

const SignUpPage = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [profileImage, setProfileImage] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const navigate = useNavigate();
    const auth = getAuth();
    const storage = getStorage();
    const db = getFirestore();

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setProfileImage(e.target.files[0]);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!name || !email || !password || !profileImage) {
            setError("Por favor, preencha todos os campos e selecione uma imagem de perfil.");
            setLoading(false);
            return;
        }

        try {
            // 1. Criar o usuário no Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 2. Fazer upload da imagem de perfil
            const imageRef = ref(storage, `profile_images/${user.uid}/${profileImage.name}`);
            await uploadBytes(imageRef, profileImage);
            const photoURL = await getDownloadURL(imageRef);

            // 3. Atualizar o perfil do Auth com nome e foto
            await updateProfile(user, {
                displayName: name,
                photoURL: photoURL
            });

            // 4. Criar o documento do usuário no Firestore
            const userDocRef = doc(db, 'users', user.uid);
            const newUser = {
                email: user.email,
                displayName: name,
                photoURL: photoURL,
                role: 'pending', // Definir o papel como pendente
                createdAt: new Date().toISOString(),
                theme: 'light',
            };
            await setDoc(userDocRef, newUser);

            // 5. Notificar os administradores
            const usersQuery = query(collection(db, 'users'), where('role', '==', 'administrador'));
            const adminSnapshot = await getDocs(usersQuery);
            const batch = writeBatch(db);
            adminSnapshot.forEach(adminDoc => {
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, {
                    userId: adminDoc.id,
                    message: `Novo usuário (${user.email}) aguardando aprovação.`,
                    type: 'newUser',
                    relatedId: user.uid,
                    createdAt: new Date().toISOString(),
                    read: false,
                });
            });
            await batch.commit();

            // A navegação para a raiz fará com que o useAuth detecte o usuário
            // e o App.js o redirecione para a página de aprovação pendente.
            navigate('/');

        } catch (error) {
            console.error("Erro no cadastro:", error);
            if (error.code === 'auth/email-already-in-use') {
                setError("Este e-mail já está em uso.");
            } else if (error.code === 'auth/weak-password') {
                setError("A senha deve ter no mínimo 6 caracteres.");
            } else {
                setError("Ocorreu um erro ao tentar se cadastrar.");
            }
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = "w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white";

    return (
        <div className="w-full min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4 font-sans">
            <div className="w-full max-w-md p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl">
                <img src={logoConnect10} alt="Logo Connect10" className="h-12 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-center text-slate-800 dark:text-slate-100 mb-2">Criar Conta</h1>
                <p className="text-center text-slate-500 dark:text-slate-400 mb-8">Preencha os dados para se cadastrar.</p>
                
                <form onSubmit={handleSignUp}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Nome de Exibição</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu Nome" className={inputStyle} required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">E-mail</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu.email@connect10.com.br" className={inputStyle} required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Senha</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputStyle} required />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Foto de Perfil</label>
                        <input type="file" onChange={handleFileChange} className={`${inputStyle} p-0 file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-200 file:text-slate-700 dark:file:bg-slate-600 dark:file:text-slate-200 hover:file:bg-slate-300 dark:hover:file:bg-slate-500`} accept="image/*" required />
                    </div>

                    {error && <p className="mb-4 text-center text-red-500 text-sm">{error}</p>}

                    <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center disabled:bg-blue-400">
                        {loading ? 'Cadastrando...' : 'Cadastrar'}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <Link to="/" className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                        Já tem uma conta? Voltar para o Login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
